// EditPerson.jsx
// Standalone page to edit a person (React Router v5 param :personId)
// Fluent UI v8 + axios (plain JS)
// - Edit any people fields: name, title, designation, default_bio, skills_overview,
//   education, affiliations, awards, employment_history, active (boolean)
// - Manage Skills (via person_skills)
// - Link Projects (via project_staff_link)
// - Link Proposals (via proposal_staff_link)
// - Search to add existing projects/proposals
//
// Expected API (adjust names as needed):
//   GET    /api/people/:personId
//   PATCH  /api/people/:personId           { ...fields }
//   GET    /api/people/:personId/skills
//   POST   /api/people/:personId/skills    { skill_id, level? }
//   DELETE /api/people/:personId/skills/:skillId
//   GET    /api/people/:personId/projects
//   POST   /api/people/:personId/projects  { project_id, role? }
//   DELETE /api/people/:personId/projects/:projectId
//   GET    /api/people/:personId/proposals
//   POST   /api/people/:personId/proposals { proposal_id, role? }
//   DELETE /api/people/:personId/proposals/:proposalId
//   GET    /api/projects?q=
//   GET    /api/proposals?q=

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link, useHistory } from 'react-router-dom';
import axios from 'axios';
import {
  Stack,
  Text,
  TextField,
  Toggle,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Separator,
  Label,
  SearchBox,
  Spinner,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  IconButton,
} from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react';
import SkillsPicker from '../components/SkillsPicker';
import ProposalLinkManager from '../components/ProposalLinkManager';
import ProjectLinkManager from '../components/ProjectLinkManager';

import { styles } from './ProposalDashboard';

const api = axios.create({ baseURL: '/api' });

// const styles = mergeStyleSets({
//   page: { maxWidth: 1200, margin: '0 auto', padding: 16 },
//   card: { border: '1px solid var(--neutralLight,#e1e1e1)', borderRadius: 12, padding: 16 },
//   chipsBox: { border: '1px solid var(--neutralLight,#e1e1e1)', borderRadius: 12, padding: 12 },
//   chipRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
//   chip: { display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 9999, height: 32, padding: '0 10px', border: '1px solid var(--neutralTertiaryAlt,#c8c8c8)' },
//   chipPrimary: { background: 'var(--themePrimary,#0078d4)', color: 'white', borderColor: 'var(--themePrimary,#0078d4)' },
//   sectionTitle: { fontWeight: 600 },
// });

export default function EditPerson() {
  const { personId } = useParams();
  const history = useHistory();

  const [form, setForm] = useState({
    name: '',
    title: '',
    designation: '',
    default_bio: '',
    skills_overview: '',
    education: '',
    affiliations: '',
    awards: '',
    employment_history: '',
    active: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setError(''); setLoading(true);
    try {
      const r = await api.get(`/people/${personId}`);
      const p = r.data || {};
      setForm({
        name: p.name || '',
        title: p.title || '',
        designation: p.designation || '',
        default_bio: p.default_bio || '',
        canonical_bio: p.canonical_bio || '',
        skills_overview: p.skills_overview || '',
        education: p.education || '',
        affiliations: p.affiliations || '',
        awards: p.awards || '',
        employment_history: p.employment_history || '',
        active: p.active !== false,
      });
    } catch (e) {
      console.error(e); setError(e?.response?.data?.error || e.message || 'Failed to load');
    } finally { setLoading(false); }
  }, [personId]);

  useEffect(() => { if (personId) load(); }, [load, personId]);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.patch(`/people/${personId}`, form);
      setSuccess('Saved');
    } catch (e) {
      console.error(e); setError(e?.response?.data?.error || e.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Stack className={styles.root} tokens={{childrenGap: 24}}>

      <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
        <Stack>
          <Text variant="xxLarge">Edit Person</Text>
          <Text variant="small" styles={{ root: { opacity: .7 } }}>Update profile, skills, projects, and proposals.</Text>
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <PrimaryButton text={saving ? 'Saving…' : 'Save'} onClick={handleSave} disabled={saving} />
          <DefaultButton text="Back" onClick={() => history.goBack()} />
        </Stack>
      </Stack>

      {loading && <Spinner label="Loading person" />}
      {!loading && error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
      {!loading && success && <MessageBar messageBarType={MessageBarType.success}>{success}</MessageBar>}

      {!loading && !error && (
        <Stack tokens={{childrenGap: 16}}>
          {/* Core fields */}
          <Stack className={styles.sectionCard}>
            <Text variant="xLarge">Profile</Text>
            <Stack tokens={{ childrenGap: 12 }}>
              <Stack horizontal tokens={{ childrenGap: 12 }}>
                <Stack.Item grow={1}>
                  <TextField label="Name" value={form.name} onChange={(_, v) => setField('name', v || '')} />
                </Stack.Item>
                <Stack.Item grow={1}>
                  <TextField label="Title" value={form.title} onChange={(_, v) => setField('title', v || '')} />
                </Stack.Item>
              </Stack>

              <Stack horizontal tokens={{ childrenGap: 12 }}>
                <Stack.Item grow={1}>
                  <TextField label="Designation" value={form.designation} onChange={(_, v) => setField('designation', v || '')} />
                </Stack.Item>
                <Stack.Item grow={1}>
                  <Toggle label="Active" checked={!!form.active} onChange={(_, checked) => setField('active', !!checked)} />
                </Stack.Item>
              </Stack>

              <TextField label="Default Bio" multiline autoAdjustHeight value={form.default_bio} onChange={(_, v) => setField('default_bio', v || '')} />
              <TextField label="Canonical Bio" multiline autoAdjustHeight value={form.canonical_bio} onChange={(_, v) => setField('canonical_bio', v || '')} />
              <TextField label="Skills Overview" multiline autoAdjustHeight value={form.skills_overview} onChange={(_, v) => setField('skills_overview', v || '')} />

              <Stack horizontal tokens={{ childrenGap: 12 }}>
                <Stack.Item grow>
                  <TextField label="Education" multiline autoAdjustHeight value={form.education} onChange={(_, v) => setField('education', v || '')} />
                </Stack.Item>
                <Stack.Item grow>
                  <TextField label="Affiliations" multiline autoAdjustHeight value={form.affiliations} onChange={(_, v) => setField('affiliations', v || '')} />
                </Stack.Item>
              </Stack>

              <Stack horizontal tokens={{ childrenGap: 12 }}>
                <Stack.Item grow>
                  <TextField label="Awards" multiline autoAdjustHeight value={form.awards} onChange={(_, v) => setField('awards', v || '')} />
                </Stack.Item>
                <Stack.Item grow>
                  <TextField label="Employment History" multiline autoAdjustHeight value={form.employment_history} onChange={(_, v) => setField('employment_history', v || '')} />
                </Stack.Item>
              </Stack>
            </Stack>
          </Stack>

          {/* Skills */}
          <Stack className={styles.sectionCard}>
            <SkillsPicker personId={personId} />
          </Stack>

          {/* Projects Linker */}
          <Stack className={styles.sectionCard}>
            <ProjectLinkManager entityType='person' entityId={personId} />
          </Stack>

          {/* Proposals Linker */}
          <Stack className={styles.sectionCard}>
            <ProposalLinkManager entityType='people' entityId={personId} />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
